import { useState } from 'react';
import dynamic from 'next/dynamic';
import { useAuthModal } from '@zoopla/authentication/use-auth-modal';
import { ArticleCollection } from '@zoopla/cms/article-collection';
import { CampaignCtaGql } from '@zoopla/landing-pages/campaign-cta';
import { FullwidthHeroLayout } from '@zoopla/layout/page';
import { usePageTracking } from '@zoopla/tracking';
import { AuthModal } from '@zoopla/authentication/modal';
import { SavedListingsWidget } from '@zoopla/saved-listings-widget';
import { contentMapper } from '../component-order-mapper/component-order-mapper';
import { HomepageHead } from '../components';
import { HomepageRegistrationFooter } from '../components/HomepageRegistrationFooter/HomepageRegistrationFooter';
import { TabbedSearch } from '../components/TabbedSearch/TabbedSearch';
import { PersonalisedBanner } from '../components/PersonalisedBanner';
import { ContentfulImageWrapper } from '../queries/heroContent.types';
import { Banner as BannerGql } from '../../graphql.generated';
import {
    widgetWrapperStyle,
    personalisedBannerWrapperStyle,
    contentBodyStyle
} from './Homepage.styles.css';

const SearchSuggestionsTownsAndCities = dynamic(
    () =>
        import('@zoopla/listing-search/suggested-towns-and-cities').then(
            (module) => module.SearchSuggestionsTownsAndCities
        ),
    { ssr: false }
);

export type HomepageProps = {
    brandHeadline: string;
    h1TitleTag: string;
    heroImage: ContentfulImageWrapper;
    isLoggedIn: boolean;
    content?: ArticleCollection[];
    contentBlock?: (ArticleCollection | CampaignCtaGql | BannerGql)[];
};

export const Homepage = (props: HomepageProps) => {
    const { isLoggedIn } = props;

    const { brandHeadline, h1TitleTag, heroImage, contentBlock } = props;
    const [isAuthModalShowing, openAuthModal, closeAuthModal] = useAuthModal();
    const [authMode, setAuthMode] = useState<'register' | 'sign-in'>('sign-in');

    usePageTracking('/homepage/', {
        section: 'homepage'
    });

    const handleOpenAuthModal = (
        triggerOrigin: React.RefObject<HTMLElement> | null,
        mode: 'register' | 'sign-in'
    ) => {
        setAuthMode(mode);
        openAuthModal(triggerOrigin);
    };

    return (
        <>
            <HomepageHead />
            <FullwidthHeroLayout
                heroBackgroundColor={
                    isLoggedIn
                        ? 'graphicErrorPageDay'
                        : 'graphicBackgroundDefault'
                }
                heroContent={
                    <TabbedSearch
                        brandHeadline={brandHeadline}
                        image={heroImage}
                        h1TitleTag={h1TitleTag}
                        isLoggedIn={isLoggedIn}
                    />
                }
                content={
                    <>
                        {isLoggedIn ? (
                            <div className={personalisedBannerWrapperStyle}>
                                <PersonalisedBanner />
                            </div>
                        ) : null}
                        <div className={contentBodyStyle}>
                            {isLoggedIn ? <SavedListingsWidget /> : null}

                            {contentBlock && contentMapper(contentBlock)}

                            {isLoggedIn ? null : (
                                <SearchSuggestionsTownsAndCities />
                            )}

                            <div className={widgetWrapperStyle}>
                                <HomepageRegistrationFooter
                                    openAuthModal={handleOpenAuthModal}
                                />
                            </div>
                        </div>
                    </>
                }
            />
            <AuthModal
                modalAuthHeading="Get more from Zoopla"
                initialMode={authMode}
                isShowing={isAuthModalShowing}
                close={closeAuthModal}
                trackingData={{
                    label: 'homepage',
                    segmentData: {
                        page: '/homepage/',
                        section: 'homepage'
                    }
                }}
            />
        </>
    );
};
